import React from 'react';
import { useDispatch } from 'react-redux';
import cn from 'classnames';
import { toUpperLatter } from '../../../../utils/utils';
import { bbCodeToHtml } from '../../../../utils/formUtils';
import {
  Visibility,
  Section as SectionProps,
  FileProps,
} from '../../../../types/common-types';

import Item from './components/items/interface';
import {
  updateItemRequest,
  submitFormRequest,
  previewFile,
} from '../../../../store/page/actions';

import Row from './components/row';
import Blackbox from './components/items/blackbox';
import Section from './components/section';
import Button from './components/items/button';
import Calendar from './components/items/calendar';
import Edit from './components/items/edit';
import Label from './components/items/label';
import Select from './components/items/select';
import Check from './components/items/check';
import Calculator from './components/items/calculator';
import Widget from './components/items/widget';
import Divider from './components/items/divider';
import List from './components/items/list';
import Table from './components/items/table';
import TableV1 from './components/items/tableV1';
import Radio from './components/items/radio';
import Phone from './components/items/phone';
import Image from './components/items/image';
import LoanInfo from './components/items/loanInfo';
import Service from './components/items/service';
import Notify from '../../../../components/notify';
import './style.scss';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ITEMS: { [key: string]: React.FC<any> } = {
  Row,
  Blackbox,
  Button,
  Edit,
  Label,
  Calendar,
  Select,
  Check,
  Calculator,
  Widget,
  Divider,
  List,
  Table,
  TableV1,
  Radio,
  Phone,
  Image,
  LoanInfo,
  Service,
};

interface FormProps {
  form: {
    id: string;
    title?: string;
    sections: SectionProps[];
    errorMsg?: string;
    visibility?: Visibility;
  };
}

const Form: React.FC<FormProps> = ({ form }) => {
  const dispatch = useDispatch();
  const updateItem = (newProps: object, callback: () => void) => {
    const itemArr = Array.isArray(newProps) ? newProps : [newProps];
    dispatch(updateItemRequest({ item: itemArr, formId: form.id, callback }));
  };
  const handleSubmit = (
    buttonId: string,
    buttonData?: Record<string, unknown>
  ) => {
    dispatch(submitFormRequest({ buttonId, form, buttonData }));
  };
  const handlePreviewFile = (file: FileProps) => {
    dispatch(previewFile({ ...file }));
  };
  const COUNTRY_CODE = process.env.COUNTRY_CODE || '';
  const renderItems = (items: Item[]) => {
    const rItems: Array<object> = [];
    let index = 0;
    items.forEach(item => {
      if (!item.class) return;
      const ItemName = toUpperLatter(item.class);
      const ItemComp = ITEMS[ItemName];
      if (!ItemComp) return;
      rItems.push(
        <ItemComp
          key={`${item.id}_${index}`}
          onChange={(newProps: object, callback: () => void) => {
            updateItem(newProps, callback);
          }}
          onSubmit={handleSubmit}
          countryCode={COUNTRY_CODE}
          previewFile={handlePreviewFile}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...item}
        />
      );
      index += 1;
    });
    return rItems;
  };

  const groupRowItems = (items: Item[]) => {
    const newItems: Item[] = [];
    const addedRows: Array<string> = [];
    items.forEach(item => {
      const rowId = item.row;
      if (!rowId) {
        newItems.push(item);
      } else if (!addedRows.includes(rowId)) {
        const rowItems = items.filter(
          i => i.row === rowId && i.visibility !== Visibility.Hidden
        );
        newItems.push({
          class: 'row',
          id: `row_${rowId}`,
          items: rowItems,
          elm: ITEMS,
        });
        addedRows.push(rowId);
      }
    });
    return newItems;
  };

  const renderSections = (sections: SectionProps[]) => {
    return sections.map((section, i) => {
      const { id, visibility, header, content, type, style } = section;
      const headerItems = groupRowItems(header || []);
      const contentItems = groupRowItems(content || []);
      const hideSectionContent =
        contentItems.every(item => item.visibility === Visibility.Hidden) ||
        contentItems.length === 0;
      const hideSectionHeader =
        headerItems.every(item => item.visibility === Visibility.Hidden) ||
        headerItems.length === 0;
      return (
        <Section
          key={`${form.id}-${id || i}`}
          id={id}
          formId={form.id}
          headerItems={renderItems(headerItems)}
          contentItems={renderItems(contentItems)}
          hideSectionHeader={hideSectionHeader}
          hideSectionContent={hideSectionContent}
          visibility={visibility}
          type={type}
          style={style}
        />
      );
    });
  };
  return (
    <div
      id={form.id}
      data-cy="form"
      styleName={cn('form', {
        hidden: form.visibility === Visibility.Hidden,
      })}
    >
      {form.errorMsg ? <Notify errors={[{ message: form.errorMsg }]} /> : null}
      {form.title ? (
        <div styleName="form__title">
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: bbCodeToHtml(form.title),
            }}
          />
        </div>
      ) : null}
      {renderSections(form.sections)}
    </div>
  );
};
export default Form;
